import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Ruler, Scales, Info, CheckCircle, ArrowSquareOut, MagnifyingGlass, Globe } from "@phosphor-icons/react";

function SourceBar({ specs }) {
  const gen = specs.generation ? ` · Generation ${specs.generation}` : "";
  const note = specs.specs_fallback
    ? specs.my26_note
    : specs.source === "otm"
      ? "Recorded by On The Move for this model. Your build details always take priority."
      : "From the official On The Move website. Your build details always take priority.";
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 bg-[#0F0F0F] border border-neutral-800 rounded-2xl px-5 py-4">
      <div className="flex items-start gap-3 min-w-0">
        <Globe size={18} className="text-[#73bf44] mt-0.5 shrink-0" weight="duotone" />
        <div className="min-w-0">
          <div className="font-heading text-sm font-semibold text-white">Standard specs · {specs.name}{gen}</div>
          <p className={`text-xs mt-0.5 ${specs.specs_fallback ? "text-amber-300" : "text-neutral-500"}`} data-testid="model-specs-source-note">{note}</p>
        </div>
      </div>
      <a href={specs.url} target="_blank" rel="noreferrer" data-testid="model-specs-website-link" className="inline-flex items-center gap-1.5 text-xs text-[#73bf44] border border-[#73bf44]/40 rounded-full px-3.5 py-1.5 hover:bg-[#73bf44]/10 transition-colors">
        View on OTM website <ArrowSquareOut size={12} />
      </a>
    </div>
  );
}

function SpecRow({ spec, big }) {
  return (
    <div data-testid="model-key-spec" className="flex items-baseline justify-between gap-4 py-3.5 border-b border-neutral-800/80 last:border-b-0">
      <span className="text-sm text-neutral-400">{spec.label}{spec.guide ? " *" : ""}</span>
      <span className={`font-heading font-semibold text-white tabular-nums whitespace-nowrap ${big ? "text-2xl" : "text-xl"}`}>{spec.value}</span>
    </div>
  );
}

function SpecCard({ icon: Icon, title, children, className = "" }) {
  return (
    <div className={`bg-[#0F0F0F] border border-neutral-800 rounded-2xl p-5 sm:p-6 ${className}`}>
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-8 h-8 rounded-lg bg-[#73bf44]/10 flex items-center justify-center"><Icon size={18} className="text-[#73bf44]" weight="duotone" /></div>
        <h3 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-[#73bf44]">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export function MeasurementsPanel({ specs }) {
  const weights = specs.key_specs.filter((k) => k.kind === "weight");
  const dims = specs.key_specs.filter((k) => k.kind !== "weight");
  return (
    <section className="space-y-5" data-testid="model-specs-section">
      <SourceBar specs={specs} />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-start">
        {dims.length > 0 && (
          <SpecCard icon={Ruler} title="Dimensions" className="lg:col-span-3">
            {dims.map((k) => <SpecRow key={k.label} spec={k} />)}
          </SpecCard>
        )}
        {weights.length > 0 && (
          <SpecCard icon={Scales} title="Weights" className={dims.length ? "lg:col-span-2" : "lg:col-span-5"}>
            {weights.map((k) => <SpecRow key={k.label} spec={k} big />)}
            <div className="flex gap-2.5 mt-4 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-3.5 text-xs text-amber-200/90 leading-relaxed" data-testid="model-specs-weight-note">
              <Info size={16} className="shrink-0 text-amber-300 mt-px" weight="fill" />
              <span>* {specs.weight_note}</span>
            </div>
          </SpecCard>
        )}
      </div>
    </section>
  );
}

function FeatureList({ group, showTitle }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} data-testid="model-feature-group">
      <div className={`font-heading text-base font-semibold text-white mb-4 ${showTitle ? "" : "lg:hidden"}`}>{group.name}</div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-6 last:mb-0">
        {group.items.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-neutral-200 leading-snug" data-testid="model-feature-item">
            <CheckCircle size={16} weight="fill" className="text-[#73bf44]/80 shrink-0 mt-px" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

function GroupNav({ groups, active, onPick, dim }) {
  return (
    <nav className={`flex lg:flex-col gap-1.5 overflow-x-auto [scrollbar-width:none] transition-opacity ${dim ? "opacity-50" : ""}`}>
      {groups.map((g, i) => {
        const on = !dim && g.name === active;
        return (
          <button
            key={g.name}
            onClick={() => onPick(g.name)}
            data-testid={`model-feature-group-toggle-${i}`}
            className={`shrink-0 flex items-center justify-between gap-4 rounded-xl px-3.5 py-2.5 text-sm text-left transition-colors duration-200 ${on ? "bg-[#73bf44]/10 text-[#73bf44] font-semibold" : "text-neutral-400 hover:text-white hover:bg-white/[0.03]"}`}
          >
            {g.name}
            <span className={`text-xs ${on ? "text-[#73bf44]" : "text-neutral-600"}`}>{g.items.length}</span>
          </button>
        );
      })}
    </nav>
  );
}

export function StandardFeatures({ specs }) {
  const [active, setActive] = useState(specs.groups[0]?.name);
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const shown = query
    ? specs.groups.map((g) => ({ ...g, items: g.items.filter((i) => i.toLowerCase().includes(query)) })).filter((g) => g.items.length)
    : specs.groups.filter((g) => g.name === active);
  const total = specs.groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <section data-testid="model-standard-features" className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-heading text-lg font-semibold text-white">Standard features · {specs.name}{specs.generation ? ` · Generation ${specs.generation}` : ""}</div>
          <p className="text-xs text-neutral-500 mt-0.5">{total} features fitted as standard to this model{specs.features_source === "otm" ? ", recorded by On The Move" : ", from the official OTM website"}.</p>
          {specs.dropped_power > 0 && (
            <p className="text-xs text-neutral-500 mt-1" data-testid="model-features-power-note">Battery, solar, inverter and management items are listed under Your Build, exactly as fitted to your van.</p>
          )}
        </div>
        <label className="flex items-center gap-2 bg-black/50 border border-neutral-800 rounded-full px-3.5 py-2 w-full sm:w-64 focus-within:border-[#73bf44] transition-colors">
          <MagnifyingGlass size={14} className="text-neutral-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search features…" data-testid="model-features-search" className="bg-transparent outline-none text-sm text-white w-full placeholder:text-neutral-600" />
        </label>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-5 lg:gap-8 mt-6">
        <GroupNav groups={specs.groups} active={active} dim={!!query} onPick={(n) => { setQ(""); setActive(n); }} />
        <div className="min-w-0 lg:border-l lg:border-neutral-800 lg:pl-8">
          <AnimatePresence mode="wait">
            {shown.length ? (
              <motion.div key={query ? `q-${query}` : active}>
                {shown.map((g) => <FeatureList key={g.name} group={g} showTitle={!!query} />)}
              </motion.div>
            ) : (
              <p key="empty" className="text-sm text-neutral-500 py-4" data-testid="model-features-empty">No standard features match "{q}".</p>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
